import type { Party } from '../engine/events'
import { PARTY_DESC, PARTY_LABEL, PARTY_PLAIN, PARTY_ROLE, type Depth } from './describe'

/**
 * The four party model drawn as it actually behaves: a square, with VisaNet
 * in the middle. The cardholder and the merchant meet along the top. Their two
 * banks sit at the bottom and never touch, which is why every line between them
 * goes through the middle.
 */

export const NODE_POS: Record<Party, { x: number; y: number }> = {
  cardholder: { x: 130, y: 70 },
  merchant: { x: 590, y: 70 },
  issuer: { x: 130, y: 330 },
  acquirer: { x: 590, y: 330 },
  scheme: { x: 360, y: 200 },
}

const BOX = { w: 200, h: 62 }

/**
 * Where the travelling marker parks relative to each node. It sits outside the
 * box rather than on top of it, so the party underneath stays readable: above
 * the top row, below the bottom row, and below Visa where the two lines
 * down to the banks have already diverged.
 */
export const MARKER_OFFSET: Record<Party, { dx: number; dy: number }> = {
  cardholder: { dx: 0, dy: -54 },
  merchant: { dx: 0, dy: -54 },
  issuer: { dx: 0, dy: 54 },
  acquirer: { dx: 0, dy: 54 },
  scheme: { dx: 0, dy: 56 },
}

export function markerPos(party: Party) {
  const base = NODE_POS[party]
  const off = MARKER_OFFSET[party]
  return { x: base.x + off.dx, y: base.y + off.dy }
}

/** Edges that exist. Anything not listed here is a conversation that never happens. */
const EDGES: [Party, Party, string][] = [
  ['cardholder', 'merchant', 'card presented'],
  ['merchant', 'acquirer', 'acquiring relationship'],
  ['acquirer', 'scheme', ''],
  ['scheme', 'issuer', ''],
  ['issuer', 'cardholder', 'account relationship'],
]

export function edgeId(a: Party, b: Party): string {
  return `edge-${[a, b].sort().join('-')}`
}

function edgeMidpoint(a: Party, b: Party) {
  const p = NODE_POS[a]
  const q = NODE_POS[b]
  return { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }
}

function node(party: Party, depth: Depth): string {
  const { x, y } = NODE_POS[party]
  const name = depth === 0 ? PARTY_PLAIN[party] : PARTY_LABEL[party]
  const role = PARTY_ROLE[party]
  const desc = PARTY_DESC[party]

  return `
    <g class="dg-node" id="node-${party}" data-party="${party}">
      <title>${desc}</title>
      <rect x="${x - BOX.w / 2}" y="${y - BOX.h / 2}" width="${BOX.w}" height="${BOX.h}" rx="2" />
      <text class="dg-role" x="${x}" y="${y - 8}" text-anchor="middle">${role.toUpperCase()}</text>
      <text class="dg-name" x="${x}" y="${y + 14}" text-anchor="middle">${name}</text>
    </g>`
}

export function diagramSvg(depth: Depth): string {
  const lines = EDGES.map(([a, b, label]) => {
    const p = NODE_POS[a]
    const q = NODE_POS[b]
    const mid = edgeMidpoint(a, b)
    const showLabel = depth !== 0 && label !== ''
    return `
      <g class="dg-edge" id="${edgeId(a, b)}">
        <line x1="${p.x}" y1="${p.y}" x2="${q.x}" y2="${q.y}" />
        ${showLabel ? `<text class="dg-edge-label" x="${mid.x}" y="${mid.y - 6}" text-anchor="middle">${label}</text>` : ''}
      </g>`
  }).join('')

  const nodes = (['cardholder', 'merchant', 'scheme', 'issuer', 'acquirer'] as Party[])
    .map((p) => node(p, depth))
    .join('')

  // The marker starts parked beside the cardholder and is moved by transform.
  const start = markerPos('cardholder')

  return `
    <svg viewBox="0 0 720 400" class="diagram" role="img"
         aria-label="The four party model. Cardholder and merchant along the top, issuer and acquirer beneath them, and VisaNet in the middle.">
      <g class="dg-edges">${lines}</g>
      ${nodes}
      <g class="dg-marker" id="dg-marker" transform="translate(${start.x}, ${start.y})" opacity="0">
        <rect class="dg-marker-box" x="-46" y="-14" width="92" height="28" rx="2" />
        <text class="dg-marker-text" id="dg-marker-text" x="0" y="5" text-anchor="middle"></text>
      </g>
    </svg>`
}
